import { Box, HStack, Icon, RippleButton, Text, Title, VStack } from "@repo/ui";
import { useTheme } from "@repo/tokens";
import { useTranslation } from "react-i18next";
import { TestModal } from "../TestModal";
import styles from "./UploadResultPopup.module.scss";

type UploadFailurePopupProps = {
  isOpen: boolean;
  onClose: () => void;
  onAction?: () => void;
};

const FAILURE_REASONS = [
  "upload_answer_sheet_flow.failure_reason_wrong_format",
  "upload_answer_sheet_flow.failure_reason_corrupted",
  "upload_answer_sheet_flow.failure_reason_size",
] as const;

export const UploadFailurePopup = ({
  isOpen,
  onClose,
  onAction,
}: UploadFailurePopupProps) => {
  const { theme } = useTheme();
  const { t } = useTranslation("tests");

  return (
    <TestModal
      isOpen={isOpen}
      onClose={onClose}
      isOverlay={true}
      width="min(95vw, 40rem)"
      body={
        <VStack className={styles.body}>
          <VStack alignItems="center" className={styles.content}>
            <Icon
              name="errorOutlineIcon"
              size={48}
              color={theme.colors.secondary[50]}
            />

            <Title
              variant="h2"
              role="headline"
              size="sm"
              weight="medium"
              color={theme.colors.text[10]}
              marginBottom={0}
              marginTop={0}
              align="center"
            >
              {t("upload_answer_sheet_flow.failure_title")}
            </Title>

            <Box className={styles.errorCard}>
              <Text
                variant="body"
                size="large"
                weight="emphasized"
                color={theme.colors.neutral[20]}
              >
                {t("upload_answer_sheet_flow.failure_subtitle")}
              </Text>
              <VStack gap="xs" className={styles.errorList}>
                {FAILURE_REASONS.map((reason) => (
                  <HStack key={reason} alignItems="flex-start" gap="sm">
                    <Box className={styles.errorBullet}>
                      <Icon
                        name="circleListIcon"
                        color={theme.colors.error[60]}
                        size={12}
                      />
                    </Box>
                    <Text
                      variant="body"
                      size="large"
                      color={theme.colors.error[60]}
                      className={styles.errorText}
                    >
                      {t(reason)}
                    </Text>
                  </HStack>
                ))}
              </VStack>
            </Box>

            <Text
              variant="body"
              size="large"
              align="center"
              color={theme.colors.neutral[20]}
              className={styles.errorText}
            >
              {t("upload_answer_sheet_flow.failure_message")}
            </Text>
          </VStack>

          <RippleButton
            variant="primary"
            size="md"
            shape="round"
            onPress={onAction ?? onClose}
          >
            {t("upload_answer_sheet_flow.reupload_pdf")}
          </RippleButton>
        </VStack>
      }
    />
  );
};
